import { useCallback, useState, useEffect, useMemo } from "react";
import { useAnalytics } from "~/components/Analytics/useAnalytics";
import {
  fetchGiftLink,
  fetchGiftLinkMonthlyBalance,
} from "~/lib/client/endilApiCalls";
import { useSession } from "next-auth/react";
import { useToastManager } from "~/components/ToastManager";
import { logger } from "~/common/logger";
import { useFeatureFlags } from "~/lib/client/useFeatureFlags";

export const useGiftLink = (postId: string | number) => {
  const { analyticsEnhancedTrack } = useAnalytics();
  const { addToast } = useToastManager();
  const { data: session } = useSession();
  const { enableGiftLink } = useFeatureFlags();
  const [userAlreadyGiftedPost, setUserAlreadyGiftedPost] = useState(false);
  const [monthlyBalance, setMonthlyBalance] = useState(0);
  const [loadingState, setLoadingState] = useState({
    fetchingBalance: false,
    generatingGiftLink: false,
    copiedFeedback: false,
  });
  const [giftLink, setGiftLink] = useState<string | null>(null);

  const canShowGiftLink = useMemo(
    () => enableGiftLink && session?.user?.email,
    [enableGiftLink, session?.user?.email]
  );

  const getGiftLink = useCallback(async () => {
    setLoadingState((prev) => ({ ...prev, generatingGiftLink: true }));
    try {
      const {
        data: { giftLink: giftLinkData, remainingQuota },
      } = await fetchGiftLink(postId);
      await navigator.clipboard.writeText(giftLinkData);
      analyticsEnhancedTrack("Gift Link Copied", { postId });
      setMonthlyBalance(remainingQuota);
      setGiftLink(giftLinkData);
      setLoadingState((prev) => ({ ...prev, copiedFeedback: true }));
    } catch (error) {
      logger.error("Error fetching gift link", { error });
      addToast("danger", "Error generating gift link");
    } finally {
      setLoadingState((prev) => ({ ...prev, generatingGiftLink: false }));
    }
  }, [addToast, analyticsEnhancedTrack, postId]);

  const getGiftLinkMonthlyBalance = useCallback(async () => {
    setLoadingState((prev) => ({ ...prev, fetchingBalance: true }));
    try {
      const { data } = await fetchGiftLinkMonthlyBalance(postId);
      setUserAlreadyGiftedPost(data.userAlreadyGiftedPost);
      setMonthlyBalance(data.balance);
    } catch (error) {
      logger.error("Error fetching gift link balance", { error, postId });
    } finally {
      setLoadingState((prev) => ({ ...prev, fetchingBalance: false }));
    }
  }, [postId]);

  const copyGiftLink = useCallback(() => {
    if (giftLink) {
      navigator.clipboard.writeText(giftLink);
      analyticsEnhancedTrack("Gift Link Copied", { postId });
      setLoadingState((prev) => ({ ...prev, copiedFeedback: true }));
    }
  }, [giftLink, analyticsEnhancedTrack, postId]);

  useEffect(() => {
    if (canShowGiftLink) {
      getGiftLinkMonthlyBalance();
    }
  }, [canShowGiftLink, getGiftLinkMonthlyBalance]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (loadingState.copiedFeedback) {
      timeout = setTimeout(() => {
        setLoadingState((prev) => ({ ...prev, copiedFeedback: false }));
      }, 1000);
    }

    return () => clearTimeout(timeout);
  }, [loadingState.copiedFeedback]);

  return {
    canShowGiftLink,
    monthlyBalance,
    userAlreadyGiftedPost,
    isLoadingBalance: loadingState.fetchingBalance,
    isGeneratingGiftLink: loadingState.generatingGiftLink,
    isCopiedFeedback: loadingState.copiedFeedback,
    getGiftLink,
    giftLink,
    copyGiftLink,
  };
};
